import { CanvasElement, IconElement } from '@/types/canvas';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ICON_NAMES } from '@/config/icons';

interface PropertiesPanelProps {
  element: CanvasElement | null;
  onUpdate: (id: string, updates: Partial<CanvasElement>) => void;
}

export function PropertiesPanel({ element, onUpdate }: PropertiesPanelProps) {
  if (!element) {
    return (
      <div className="w-72 border-r bg-card p-4 flex items-center justify-center h-full">
        <p className="text-muted-foreground">Select an element to edit properties</p>
      </div>
    );
  }

  const handleChange = (updates: Partial<CanvasElement>) => {
    onUpdate(element.id, updates);
  };

  return (
    <div className="w-72 border-r bg-card flex flex-col h-full">
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-4">
          <div>
            <h3 className="mb-4">Element Properties</h3>
          </div>

          {/* Position */}
          <div className="space-y-2">
            <Label>Position</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">X</Label>
                <Input
                  type="number"
                  value={Math.round(element.x)}
                  onChange={(e) => handleChange({ x: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Y</Label>
                <Input
                  type="number"
                  value={Math.round(element.y)}
                  onChange={(e) => handleChange({ y: Number(e.target.value) })}
                />
              </div>
            </div>
          </div>

          {/* Size */}
          <div className="space-y-2">
            <Label>Size</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Width</Label>
                <Input
                  type="number"
                  value={Math.round(element.width)}
                  onChange={(e) => handleChange({ width: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Height</Label>
                <Input
                  type="number"
                  value={Math.round(element.height)}
                  onChange={(e) => handleChange({ height: Number(e.target.value) })}
                />
              </div>
            </div>
          </div>

          {/* Rotation */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Rotation</Label>
              <span className="text-xs text-muted-foreground">{Math.round(element.rotation)}°</span>
            </div>
            <Slider
              value={[element.rotation]}
              onValueChange={([rotation]) => handleChange({ rotation })}
              min={0}
              max={360}
              step={1}
            />
          </div>

          {/* Type-specific properties */}
          {element.type === 'icon' && (
            <>
              <div className="space-y-2">
                <Label>Icon Type</Label>
                <Select
                  value={(element as IconElement).iconName}
                  onValueChange={(value) => handleChange({ iconName: value } as Partial<IconElement>)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_NAMES.map((icon) => (
                      <SelectItem key={icon} value={icon}>
                        {icon}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={(element as IconElement).color}
                    onChange={(e) => handleChange({ color: e.target.value } as Partial<IconElement>)}
                    className="w-16 h-10"
                  />
                  <Input
                    value={(element as IconElement).color}
                    onChange={(e) => handleChange({ color: e.target.value } as Partial<IconElement>)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Label (optional)</Label>
                <Input
                  value={(element as IconElement).label || ''}
                  onChange={(e) => handleChange({ label: e.target.value } as Partial<IconElement>)}
                  placeholder="e.g., Home"
                />
              </div>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

